import React, {useEffect, useState} from 'react';
import OeTip from './OeTip';
import DocPanel from './DocPanel';
import {confirmArrived, kommCreate, kommWizard, loadArrived, loadDocs, orderReopen} from './api';
import {
    WH_LAHR,
    findKommDocs,
    kommFingerprint,
    kommFpOf,
    schneiderLsDone,
    splitPlan,
} from './wizard';

// Schritt 4 des geführten Ablaufs: Kommissionierschein für Lahr erzeugen und
// ans Lager geben. Original `renderKommBar` (3420–3496), `doKommWizard`
// (4437), `createKommSchein` (3367), `offerReopen` (3405) und der Zweig
// `WIZ_STEP === 4` in `renderWarehouse` (4300–4320).
// ABGLEICH E62–E65, G16–G20, G50, J49–J51, A13, A14.

/** Beschriftung und Hinweis je Schein-Variante (Original `meta`, 3437). */
function metaOf(doc) {
    const grouped = /zusammengefasst/i.test(doc.name || '');
    return grouped
        ? {
            label: 'Kommissionierschein – zusammengefasst',
            hint: <>💡 Zum Kontroll-Zählen: gleiche Gebinde nach Warengruppe gebündelt (Mengensumme je WG).</>,
        }
        : {
            label: 'Kommissionierschein – einzeln',
            hint: <>💡 Zum Picken: jede Artikelnummer einzeln mit eigener Menge. Rücklauf kommt in Schritt
                {' '}„Kommissionierung" (6).</>,
        };
}

export default function StepKommischein({
    ctx,
    wizard,
    orderRef,
    dispatch,
    toast,
    abilities,
    poll,
    onRefreshDocMeta,
}) {
    const {order, rows, stock} = ctx;
    const plan = splitPlan(WH_LAHR, rows, stock);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [reopenOffen, setReopenOffen] = useState(false);

    const canWarehouse = !!(abilities && abilities.warehouse);
    const kommState = wizard.kommState;
    const kdocs = kommState ? ((kommState.kommDocs && kommState.kommDocs.length)
        ? kommState.kommDocs
        : (kommState.komm ? [kommState.komm] : [])) : [];

    // „Angekommen"-Vermerk erst holen, wenn der Block überhaupt gezeigt wird
    // (Original `loadArrived`, 3205).
    const stagedFlow = !!(plan && plan.secondaryRows.length > 0);
    const zeigeArrived = stagedFlow && wizard.orderSource === 'lahr';
    const arrivedGeladen = wizard.arrived.id === String(order.orderId);

    useEffect(() => {
        if (!zeigeArrived || arrivedGeladen || wizard.arrived.loading) {
            return;
        }
        dispatch({type: 'wiz-patch', patch: {arrived: {id: String(order.orderId), data: null, loading: true}}});
        loadArrived(orderRef)
            .then((j) => dispatch({
                type: 'wiz-patch',
                patch: {arrived: {id: String(order.orderId), data: j && j.ok ? j : null, loading: false}},
            }))
            .catch(() => dispatch({
                type: 'wiz-patch',
                patch: {arrived: {id: String(order.orderId), data: null, loading: false}},
            }));
    }, [zeigeArrived, arrivedGeladen, wizard.arrived.loading, order.orderId, orderRef, dispatch]);

    if (!plan) {
        return null;
    }

    const fingerprint = kommFingerprint(plan);

    /** Dokumente am Beleg neu suchen und die Schein-Liste fortschreiben. */
    const scheineSuchen = async () => {
        const d = await loadDocs(orderRef, {type: 'order', id: order.orderId}).catch(() => ({}));
        const docs = d.docs || [];
        const gefunden = findKommDocs(docs);
        dispatch({
            type: 'wiz-patch',
            patch: {
                kommState: {...(kommState || {}), docs, komm: gefunden[0] || null, kommDocs: gefunden},
                progress: {
                    kommDoc: docs.some((x) => /kommission/i.test(x.name || '')),
                    abDoc: docs.some((x) => /auftragsbest|sammelpos/i.test(x.name || '')),
                    docs,
                },
            },
        });
        return gefunden;
    };

    /** Beide Scheine über den Hintergrund-Job erzeugen (Original `doKommWizard`). */
    const erzeugen = async () => {
        setBusy(true);
        setStatus({tone: 'work', text: 'Kommissionierschein wird erzeugt…'});
        try {
            const antwort = await kommWizard(orderRef, {
                lahrItemIds: plan.primaryRows.map((r) => String(r.id)),
                schneiderItemIds: plan.secondaryRows.map((r) => String(r.id)),
                fingerprint,
            });
            const daten = await poll(antwort.jobId, {
                interval: 1500,
                onProgress: (d) => {
                    const p = d && d.payload && d.payload.progress;
                    if (p && p.phase) {
                        setStatus({tone: 'work', text: `${p.phase}…`});
                    }
                },
            });
            const j = (daten && daten.result) || {};

            if (!j.ok) {
                setStatus({tone: 'err', text: 'Fehler: ' + (j.error || 'unbekannt')});
                return;
            }

            const trockenlauf = (j.docs || []).some((d) => d.dryRun);
            if (trockenlauf) {
                setStatus({
                    tone: 'warn',
                    text: 'Trockenlauf: Die Scheine wurden gerechnet, aber nicht an den Auftrag gehängt.',
                });
                toast('Trockenlauf: Der Kommissionierschein wurde nicht an den Auftrag gehängt.');
                return;
            }

            dispatch({type: 'wiz-komm', patch: {komm: true}});
            toast(`✓ Kommissionierschein erzeugt (${j.lahrPos} Lahr-Pos.`
                + `${j.schneiderPos ? `, ${j.schneiderPos} ab Schneider` : ''}) — als „${j.name || 'PDF'}" an den `
                + 'Auftrag gehängt.');
            onRefreshDocMeta([]);

            const gefunden = await scheineSuchen();
            setStatus({
                tone: 'ok',
                text: gefunden.length > 1
                    ? '✓ Kommissionierscheine (zusammengefasst + einzeln) erzeugt'
                    : '✓ Kommissionierschein erzeugt',
            });
        } catch (e) {
            const t = e.transmission || {};
            const meldung = (t.result && t.result.error) || t.error_message || e.response?.data?.error || e.message;
            setStatus({tone: 'err', text: 'Fehler: ' + meldung});
        } finally {
            setBusy(false);
        }
    };

    /**
     * Rückfallebene des Originals (`createKommSchein`, 3367): Checkbox am
     * Auftrag setzen und festschreiben, der n8n-Ablauf erzeugt das PDF. Im
     * Original saß dieser Weg außerhalb von Schritt 4 — hier steht er als
     * zweiter Knopf daneben, sonst wäre er nie erreichbar.
     */
    const ueberCheckbox = async () => {
        setBusy(true);
        setStatus({tone: 'work', text: 'Auftrag wird festgeschrieben & Checkbox gesetzt…'});
        try {
            const j = await kommCreate(orderRef);
            if (!j.ok) {
                setStatus({tone: 'err', text: 'Fehlgeschlagen: ' + (j.error || 'unbekannt')});
                return;
            }
            if (j.dryRun) {
                setStatus({tone: 'warn', text: `Trockenlauf: ${j.message || 'Die Checkbox wurde nicht gesetzt.'}`});
                toast(`Trockenlauf: ${j.message || 'Die Checkbox wurde nicht gesetzt.'}`);
                return;
            }
            setStatus({
                tone: 'work',
                text: (j.already ? 'Checkbox war schon gesetzt.' : 'Festgeschrieben & Checkbox gesetzt.')
                    + (j.confirmed ? ' Auftrag wurde festgeschrieben.' : '') + ' Der Schein wird erzeugt…',
            });
            if (j.confirmed) {
                setReopenOffen(true);
            }
            onRefreshDocMeta([]);
            const gefunden = await scheineSuchen();
            setStatus(gefunden.length
                ? {tone: 'ok', text: '✓ Kommissionierschein erzeugt'}
                : {
                    tone: 'warn',
                    text: 'Noch kein PDF da — braucht evtl. etwas länger. Mit „↻ neu suchen" nochmal prüfen.',
                });
        } catch (e) {
            setStatus({tone: 'err', text: (e.response?.data?.error || e.message)});
        } finally {
            setBusy(false);
        }
    };

    /** Festschreiben zurücknehmen (Original `offerReopen`, 3405). */
    const reopen = async () => {
        setBusy(true);
        setStatus({tone: 'work', text: 'Wird zurückgesetzt…'});
        try {
            const j = await orderReopen(orderRef);
            if (j.ok) {
                setStatus({tone: 'ok', text: '✓ Auftrag wieder auf „Auftragserfassung".'});
                setReopenOffen(false);
                onRefreshDocMeta([]);
                return;
            }
            setStatus({tone: 'err', text: 'Rückgängig fehlgeschlagen: ' + (j.error || 'unbekannt')});
        } catch (e) {
            setStatus({tone: 'err', text: (e.response?.data?.error || e.message)});
        } finally {
            setBusy(false);
        }
    };

    const vermerkSetzen = async () => {
        setBusy(true);
        toast('Vermerk wird am Auftrag gespeichert …');
        try {
            const j = await confirmArrived(orderRef);
            if (j.dryRun) {
                toast(`Trockenlauf: ${j.message || 'Der Vermerk wurde nicht geschrieben.'}`);
                return;
            }
            if (j.ok) {
                dispatch({
                    type: 'wiz-patch',
                    patch: {arrived: {id: String(order.orderId), data: {ok: true, confirmed: true, text: j.text}, loading: false}},
                });
                dispatch({type: 'wiz-komm', patch: {arrived: true}});
                toast('✓ „Angekommen" am Auftrag vermerkt');
                return;
            }
            toast('✕ ' + (j.error || 'Fehler'), {variant: 'danger'});
        } catch (e) {
            toast('✕ ' + (e.response?.data?.error || e.message), {variant: 'danger'});
        } finally {
            setBusy(false);
        }
    };

    // ── Kommissionierschein-Leiste ──────────────────────────────────────────
    const erzeugenBtn = (
        <OeTip
            tip={canWarehouse
                ? 'Erzeugt den Kommissionierschein und hängt ihn als PDF an den Auftrag.'
                : 'Zum Erzeugen des Kommissionierscheins fehlt das Recht „Lager".'}
            detail={canWarehouse
                ? 'Enthält die Lahr-Positionen und, falls vorhanden, den Schneider-Hinweis. Umkehrbar — ein neuer Lauf hängt einen neuen Schein an. Im Testmodus wird nichts angehängt („Trockenlauf").'
                : undefined}
        >
            <button type="button" className="actbtn" disabled={busy || !canWarehouse} onClick={erzeugen}>
                📋 Kommissionierschein {kdocs.length ? 'neu erzeugen' : 'erzeugen'}
            </button>
        </OeTip>
    );

    const suchenBtn = (
        <OeTip tip="Sucht die Dokumente am Auftrag erneut — für den Fall, dass weclapp noch am Rendern war. Es wird nichts erzeugt.">
            <button type="button" className="actbtn" disabled={busy} onClick={() => scheineSuchen()}>
                ↻ neu suchen
            </button>
        </OeTip>
    );

    let kommBar;
    if (kommState === null) {
        kommBar = <span className="muted">🖨 Kommissionierschein wird geladen…</span>;
    } else if (!kdocs.length) {
        kommBar = (
            <div style={{width: '100%'}}>
                <div className="kommhead">
                    <span className="lbl">🖨 Kommissionierschein</span>
                    <span className="muted">für diesen Auftrag noch nicht erzeugt</span>
                    <span className="sp"/>
                    {erzeugenBtn}
                    {suchenBtn}
                </div>
                {status ? <div className={`status ${status.tone}`} style={{marginTop: 6}}>{status.text}</div> : null}
                {reopenOffen ? (
                    <OeTip tip={'Setzt den Auftrag zurück auf „Auftragserfassung" — er ist danach wieder bearbeitbar.'}>
                        <button type="button" className="link" disabled={busy} onClick={reopen}>
                            ↩ Festschreiben rückgängig
                        </button>
                    </OeTip>
                ) : null}
                <div className="kommhelp">
                    💡 Erzeugt den Kommissionierschein (Lahr-Positionen) und hängt ihn an den Auftrag. Danach
                    erscheint hier die Vorschau zum Drucken.
                    {canWarehouse ? (
                        <>
                            {' '}Geht es ausnahmsweise über die <b>Checkbox „Kommissionierschein" am Auftrag in
                            weclapp</b>, wird der Auftrag dabei <b>festgeschrieben</b> — das lässt sich mit einem
                            Klick wieder rückgängig machen:{' '}
                            <OeTip
                                tip={'Setzt am Auftrag die Checkbox „Kommissionierschein" und schreibt den Auftrag fest — daraufhin erzeugt weclapp das PDF.'}
                                detail={'Das PDF entsteht im weclapp-Ablauf. Das Festschreiben lässt sich danach zurücknehmen. Im Testmodus läuft das als Trockenlauf.'}
                            >
                                <button type="button" className="link" disabled={busy} onClick={ueberCheckbox}>
                                    ✚ über die weclapp-Checkbox erzeugen
                                </button>
                            </OeTip>
                        </>
                    ) : null}
                </div>
            </div>
        );
    } else {
        const embFps = kdocs.map((d) => kommFpOf(d.name));
        const stale = embFps.some((fp) => fp && fp !== fingerprint);
        const noFp = embFps.every((fp) => !fp);

        kommBar = (
            <div style={{width: '100%'}}>
                {stale ? (
                    <div className="status err" style={{marginBottom: 10}}>
                        <b>⚠ Schein weicht vom Auftrag ab</b> — der Auftrag wurde nach dem Erzeugen geändert
                        (Positionen/Mengen/Lahr–Schneider). Bitte „neu erzeugen".
                    </div>
                ) : (
                    <div className="kommhelp" style={{marginBottom: 10}}>
                        {noFp
                            ? 'ℹ Aktualität dieser Scheine nicht prüfbar (vor dem Update erzeugt) — im Zweifel neu erzeugen.'
                            : '✓ Scheine stimmen mit dem aktuellen Auftrag überein.'}
                    </div>
                )}

                {kdocs.map((doc, i) => {
                    const m = metaOf(doc);
                    return (
                        <div key={doc.docId || i} style={i ? {marginTop: 14} : undefined}>
                            <DocPanel
                                doc={doc}
                                icon="🖨"
                                label={m.label}
                                hint={m.hint}
                                orderRef={orderRef}
                                source={{type: 'order', id: order.orderId}}
                                kommState={kommState}
                                canPrint={canWarehouse}
                                onPreferred={(name) => dispatch({
                                    type: 'wiz-patch',
                                    patch: {kommState: {...(kommState || {}), preferred: name}},
                                })}
                                extra={i === 0 ? <>{erzeugenBtn}{suchenBtn}</> : null}
                            />
                        </div>
                    );
                })}

                {status ? <div className={`status ${status.tone}`} style={{marginTop: 6}}>{status.text}</div> : null}
                {reopenOffen ? (
                    <OeTip tip={'Setzt den Auftrag zurück auf „Auftragserfassung" — er ist danach wieder bearbeitbar.'}>
                        <button type="button" className="link" disabled={busy} onClick={reopen}>
                            ↩ Festschreiben rückgängig
                        </button>
                    </OeTip>
                ) : null}
            </div>
        );
    }

    // ── „Angekommen bestätigen" (optional) ──────────────────────────────────
    const arrived = !!(wizard.arrived.data && wizard.arrived.data.confirmed);
    const arrivedLaedt = wizard.arrived.loading || !arrivedGeladen;
    const sDone = schneiderLsDone(ctx);

    return (
        <>
            <div className="fbar" style={{display: 'flex'}}>{kommBar}</div>

            {zeigeArrived ? (
                <div className="liefergo staged">
                    <div className={`stagestep${arrived ? ' done' : ''}`}>
                        <div className="stagehead">
                            <b>Angekommen bestätigen</b> <span className="muted">· optional</span>
                        </div>
                        {arrivedLaedt ? (
                            <span className="muted">Status wird geprüft …</span>
                        ) : (
                            <OeTip
                                tip={arrived
                                    ? 'Der Vermerk steht bereits als Kommentar am Auftrag.'
                                    : (sDone
                                        ? 'Schreibt einen Vermerk (Kommentar) am Auftrag, dass die Schneider-Ware in Lahr eingetroffen ist.'
                                        : 'Erst wenn der Schneider-Lieferschein erzeugt ist, kann Ware in Lahr ankommen.')}
                                detail={!arrived && sDone
                                    ? 'Für alle sichtbar, keine Voraussetzung für den Kommissionierschein. Im Testmodus wird nichts geschrieben.'
                                    : undefined}
                            >
                                <button
                                    type="button"
                                    className={`chkbtn${arrived ? ' on' : ''}`}
                                    disabled={!sDone || arrived || busy || !canWarehouse}
                                    onClick={vermerkSetzen}
                                >
                                    {arrived ? '✓ ' : ''}Alle Schneider-Artikel sind in Lahr angekommen
                                </button>
                            </OeTip>
                        )}
                        <div className="lg-desc">
                            Schreibt einen <b>Vermerk (Kommentar) am Auftrag</b> — für alle sichtbar.{' '}
                            <b>Keine Voraussetzung</b> für den Kommischein.
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}
